import { MathUtils, OrthographicCamera, Raycaster, Vector2, Vector3 } from 'three';
import { CAMERA_ZOOM_RANGE } from './isometric-camera.js';

const EDGE_WIDTH = 28;
const EDGE_SPEED = 650; // CSS pixels per second; world distance follows zoom.
const ARROWS: Record<string, [number, number]> = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };

export class CameraControls {
  /** How far the wheel may zoom: `CAMERA_ZOOM_RANGE` everywhere; only the scenario viewer widens it. */
  zoomRange: { min: number; max: number } = CAMERA_ZOOM_RANGE;
  private listeners = new AbortController();
  private pointer: { x: number; y: number; buttons: number } | null = null;
  private drag: { x: number; y: number; id: number } | null = null;
  /** Arrow keys held down; each pans the camera at the edge-scroll speed. */
  private arrows = new Set<string>();
  private raycaster = new Raycaster();
  private from = new Vector3();
  private to = new Vector3();
  private screen = new Vector2();

  constructor(private camera: OrthographicCamera, private canvas: HTMLCanvasElement,
    private enabled: () => boolean, private selecting: () => boolean) {
    const surface = canvas.parentElement!, document = canvas.ownerDocument;
    const options = { signal: this.listeners.signal };
    document.addEventListener('pointermove', event => {
      if (event.pointerType !== 'mouse') return;
      this.pointer = { x: event.clientX, y: event.clientY, buttons: event.buttons };
      const drag = this.drag;
      if (!drag || event.pointerId !== drag.id) return;
      if (!(event.buttons & 4)) { this.stopDrag(); return; }
      this.pan(drag.x - event.clientX, drag.y - event.clientY);
      drag.x = event.clientX; drag.y = event.clientY;
    }, options);
    // Only the middle button drags the camera; the right button is the radial menu's.
    surface.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 1 || !enabled() || selecting() || this.drag) return;
      event.preventDefault();
      this.drag = { x: event.clientX, y: event.clientY, id: event.pointerId };
      this.pointer = null;
      if (!document.pointerLockElement) canvas.setPointerCapture(event.pointerId);
      canvas.style.cursor = 'var(--cursor-grab, grabbing)';
    }, options);
    const endPointer = (event: PointerEvent) => {
      if (event.pointerId === this.drag?.id && (event.type !== 'pointerup' || event.button === 1)) {
        this.stopDrag(); this.pointer = null;
      }
    };
    document.addEventListener('pointerup', endPointer, options);
    document.addEventListener('pointercancel', endPointer, options);
    canvas.addEventListener('lostpointercapture', endPointer, options);
    // Arrow keys pan unless a control that uses them (a text field, the pack's tabs) has the focus.
    document.addEventListener('keydown', event => {
      if (!(event.code in ARROWS) || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if ((event.target as HTMLElement | null)?.closest?.('input, textarea, select, [role="tab"], [contenteditable="true"]')) return;
      this.arrows.add(event.code); event.preventDefault();
    }, options);
    document.addEventListener('keyup', event => { this.arrows.delete(event.code); }, options);
    const reset = () => { this.pointer = null; this.arrows.clear(); this.stopDrag(); };
    document.addEventListener('pointerleave', reset, options);
    document.defaultView!.addEventListener('blur', reset, options);
    document.addEventListener('visibilitychange', reset, options);
    // Prevent native middle-click autoscroll and navigation, including on world labels.
    surface.addEventListener('mousedown', event => { if (event.button === 1) event.preventDefault(); }, options);
    surface.addEventListener('auxclick', event => { if (event.button === 1) event.preventDefault(); }, options);
    surface.addEventListener('contextmenu', event => event.preventDefault(), options);
    canvas.addEventListener('wheel', event => {
      event.preventDefault();
      camera.zoom = MathUtils.clamp(camera.zoom * Math.exp(-event.deltaY * .001), this.zoomRange.min, this.zoomRange.max);
      camera.updateProjectionMatrix();
    }, { ...options, passive: false });
  }

  get dragging() { return this.drag !== null; }

  /**
   * The canvas's bounds, measured again only after it or the window changes size: reading them every frame forced a layout
   * after the frame's label and overlay writes, most of a millisecond in a crowded fight.
   */
  private bounds() {
    // Without a way to hear of size changes (tests), every read is fresh.
    if (typeof ResizeObserver !== 'function') return this.canvas.getBoundingClientRect();
    if (!this.rect) {
      this.rect = this.canvas.getBoundingClientRect();
      if (!this.watching) {
        this.watching = true;
        const forget = () => { this.rect = null; };
        new ResizeObserver(forget).observe(this.canvas);
        this.canvas.ownerDocument.defaultView!.addEventListener('resize', forget, { signal: this.listeners.signal });
      }
    }
    return this.rect;
  }
  private rect: DOMRect | null = null;
  private watching = false;

  update(seconds: number) {
    const document = this.canvas.ownerDocument;
    if (this.drag || !this.enabled() || this.selecting() || document.hidden) return;
    const pointer = this.pointer;
    if (!this.arrows.size && (!pointer || pointer.buttons)) return;
    const rect = this.bounds(), distance = EDGE_SPEED * MathUtils.clamp(seconds, 0, .05);
    if (this.arrows.size) {
      let dx = 0, dy = 0;
      for (const code of this.arrows) { dx += ARROWS[code][0]; dy += ARROWS[code][1]; }
      const length = Math.hypot(dx, dy);
      if (length) { this.pan(dx / length * distance, dy / length * distance, rect); return; }
    }
    if (!pointer || pointer.buttons) return;
    const x = pointer.x - rect.left, y = pointer.y - rect.top;
    if (x < 0 || y < 0 || x > rect.width || y > rect.height || !rect.width || !rect.height) return;
    const strength = (position: number, size: number) => {
      const band = Math.min(EDGE_WIDTH, size / 2);
      return Math.max(0, 1 - (size - position) / band) - Math.max(0, 1 - position / band);
    };
    let dx = strength(x, rect.width), dy = strength(y, rect.height);
    const length = Math.hypot(dx, dy);
    if (!length) return;
    // Only edge pointers need hit testing; newly opened UI must stop scrolling too.
    const target = document.elementFromPoint(pointer.x, pointer.y);
    if (!target || !this.canvas.parentElement!.contains(target)) return;
    if (length > 1) { dx /= length; dy /= length; }
    this.pan(dx * distance, dy * distance, rect);
  }

  dispose() {
    this.listeners.abort(); this.pointer = null; this.arrows.clear(); this.stopDrag();
  }

  private stopDrag() {
    const drag = this.drag; this.drag = null;
    this.canvas.style.cursor = '';
    if (drag && this.canvas.hasPointerCapture(drag.id)) this.canvas.releasePointerCapture(drag.id);
  }

  private pan(dx: number, dy: number, rect = this.bounds()) {
    if (!rect.width || !rect.height) return;
    this.camera.updateMatrixWorld(true);
    this.raycaster.setFromCamera(this.screen.set(0, 0), this.camera);
    const ray = this.raycaster.ray;
    if (Math.abs(ray.direction.y) < 1e-6) return;
    ray.at(-ray.origin.y / ray.direction.y, this.from);
    this.raycaster.setFromCamera(this.screen.set(dx / rect.width * 2, -dy / rect.height * 2), this.camera);
    // Use the infinite projection line: large zoomed-out drags can cross the near plane.
    ray.at(-ray.origin.y / ray.direction.y, this.to);
    this.camera.position.add(this.to.sub(this.from));
    this.camera.updateMatrixWorld(true);
  }
}
